import React, {useEffect, useMemo, useState} from 'react'
import {observer} from 'mobx-react'
import {Breadcrumbs} from '../../components/breadcrumbs'
import {Layout} from '../../components/layout'
import {Box, Container, Stack} from '../../vanilla'
import {YourDelivery} from './partials/your-delivery'
import {BookingSlots} from './partials/booking-slots'
import {RapidDelivery} from './partials/rapid-delivery'
import {TObservablePage} from '../../types/commerce'
import {Seo} from '../../components/seo'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {Slot} from '../../components/cms/Slot'
import {SlotContentItem} from '../../types/cms'
import {PreBookBanner} from './partials/pre-book-banner'
import {PreBookActiveReservation} from '../pre-book-delivery/partials/pre-book-active-reservation'
import {isMobile} from '../../utils/utils'
import {
  useBasketStore,
  useContentStore,
  useCustomerStore,
  useGlobalStore,
  usePreBookStore,
  useRapidDeliveryStore
} from '../../store/hooks/useStore'
import useDeliveryBooking from '../../hooks/use-delivery-booking'
import useNavigation from '../../hooks/use-navigation'
import {useLocation} from 'react-router-dom'

const BookDelivery: TObservablePage = () => {
  const location = useLocation<{originPath?: string}>()
  const originPath = location?.state?.originPath
  const {
    BASFooters,
    BASHeroes,
    contentById,
    fetchItemsByContentType,
    fetchItemByKey,
    validBASFooters,
    validBASHeroes
  } = useContentStore()
  const {deliveryPostalCode, deliverySlotPostalCode, deliverySlotArray, status} = useBasketStore()
  const { getCustomSitePreferenceById, isCustomSitePreferenceSet, customSitePreferences } = useGlobalStore()
  const {getDeliverySlots, loading: loadingDelivery} = useDeliveryBooking()
  const {hasValidPreBookedSlot, prebookedOrder} = usePreBookStore()
  const {isRegistered, loading: loadingCustomer} = useCustomerStore()
  const {isRapidDeliveryAvailableForPostcode} = useRapidDeliveryStore()
  const sendPageData = usePageDataTracking()
  const pageType = 'bookaslot'
  const pageCategory = 'Delivery'
  const errorStatusOnDeliverySlots = status === 'error'
  const [isPreBookEnabled, setPreBookEnabled] = useState<boolean>(false)
  const [isPreBookForNewCustomersEnabled, setPreBookForNewCustomersEnabled] = useState<boolean>(false)
  const additionalInfoDeliveryKey = 'book-a-slot-additional-information'
  const additionalInfoDeliveryContent = contentById[additionalInfoDeliveryKey]
  const rapidAdditionalInfoDeliveryKey = 'rapid-slot-additional-information'
  const rapidAdditionalInfoDeliveryContent = contentById[rapidAdditionalInfoDeliveryKey]

  const showPreBookBanner = useMemo(() => {
    return isPreBookEnabled && isRegistered && isPreBookForNewCustomersEnabled && !hasValidPreBookedSlot
  }, [isPreBookEnabled, isRegistered, isPreBookForNewCustomersEnabled, hasValidPreBookedSlot])

  const hasActivePreBooking = useMemo(() => {
    return isPreBookEnabled && hasValidPreBookedSlot
  }, [isPreBookEnabled, hasValidPreBookedSlot, prebookedOrder])

  useEffect(() => {
    if (loadingDelivery) return

    // refresh slots data when postcode changes
    if (deliveryPostalCode || deliverySlotPostalCode !== deliveryPostalCode) {
      getDeliverySlots()
    }
  }, [deliveryPostalCode, deliverySlotPostalCode])

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [])

  useEffect(() => {
    if (BASHeroes?.length < 1) {
      fetchItemsByContentType(
        'https://www.iceland.co.uk/slot/book-delivery-hero-slot.json',
        'BASHeroes',
      )
    }
    if (BASFooters?.length < 1) {
      fetchItemsByContentType(
        'https://www.iceland.co.uk/slot/book-delivery-information-slot.json',
        'BASFooters',
      )
    }
  }, [BASHeroes, BASFooters])

  useEffect(() => {
    (async function () {
      if (!additionalInfoDeliveryContent) {
        await fetchItemByKey(additionalInfoDeliveryKey)
      }
      if (!rapidAdditionalInfoDeliveryContent) {
        await fetchItemByKey(rapidAdditionalInfoDeliveryKey)
      }
    })()
  }, [additionalInfoDeliveryContent, rapidAdditionalInfoDeliveryContent])

  // Re-direct if customer is not logged in
  const navigate = useNavigation()
  useEffect(() => {
    if (!loadingCustomer && !isRegistered) {
      // Ensure customer returns to this page once logged
      navigate('/login', 'push', {
        directedFrom: '/book-delivery',
        originPath,
      })
      return
    }
  }, [loadingCustomer, isRegistered, originPath])

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('preBookEnabled')) {
        await getCustomSitePreferenceById('preBookEnabled')
      }
      if (!isCustomSitePreferenceSet('preBookForNewCustomersEnabled')) {
        await getCustomSitePreferenceById('preBookForNewCustomersEnabled')
      }
      setPreBookEnabled(customSitePreferences['preBookEnabled'] as boolean)
      setPreBookForNewCustomersEnabled(customSitePreferences['preBookForNewCustomersEnabled'] as boolean)
    })()
  })

  const rapidSlotData = isRapidDeliveryAvailableForPostcode && rapidAdditionalInfoDeliveryContent
    ? {
        ...rapidAdditionalInfoDeliveryContent,
        alignment: isMobile() ? 'Center' : rapidAdditionalInfoDeliveryContent.alignment,
      }
    : undefined

  return (
    <Layout>
      <Seo
        title="Book a Delivery Slot | Iceland Delivery"
        description="Book a delivery slot with Iceland and receive your order at a time that suits you."
      />
      <Container paddingX={['0px', '8px']}>
        <Box paddingX="8px" paddingY="12px">
          <Breadcrumbs breadcrumbs={[{id: 'BOOK_DELIVERY', name: 'Book your delivery'}]} />
        </Box>

        <Stack
          display='flex'
          gap={deliverySlotArray?.length ? '10px' : ['24px', '40px']}
          marginBottom={deliverySlotArray?.length ? '0px' : '40px'}
        >
          {validBASHeroes && (
            <Slot
              data={validBASHeroes[0] as unknown as SlotContentItem}
              key={validBASHeroes[0]?._meta?.deliveryKey}
            />
          )}

          {hasActivePreBooking && <PreBookActiveReservation />}
          <YourDelivery />
          {/* Rapid Delivery */}
          {isRapidDeliveryAvailableForPostcode && <RapidDelivery />}
          {/* Content Slot - Rapid Additional Charge Info */}
          {rapidSlotData && (
            <Box
              display='flex'
              paddingX={['16px', '12px']}
              paddingY={['10px', '12px']}
              flexDirection={['column', 'row']}
              data-test-selector="rapid-delivery-additional-info"
            >
              <Slot data={rapidSlotData} key={rapidAdditionalInfoDeliveryKey} />
            </Box>
          )}
          <BookingSlots />
          {showPreBookBanner && <PreBookBanner />}
          {/* Content Slot - Additional Charge Info */}
          {additionalInfoDeliveryContent && 
            <Box display='block' paddingX={['16px', '12px']} paddingY={['10px', '12px']}>
              <Slot 
                data={additionalInfoDeliveryContent}
                key={additionalInfoDeliveryKey}
              />
            </Box>
          }
          {BASFooters && validBASFooters && !errorStatusOnDeliverySlots && (
            <Box display='block' paddingBottom={deliverySlotArray?.length ? '20px' : '0px'}>
              <Slot
                data={validBASFooters[0] as unknown as SlotContentItem}
                key={validBASFooters[0]?._meta?.deliveryKey}
              />
            </Box>
          )}
        </Stack>
      </Container>
    </Layout>
  )
}

export default observer(BookDelivery)
